import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BookOpen, PenLine, Trash2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { EmptyState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { academicSubjectsApi } from '@/services/api/endpoints/academic';
import { examinationsApi } from '@/services/api/endpoints/examinations';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import type { Examination, ExaminationSubject } from '@/types/examination';

const schema = z
  .object({
    academicSubjectId: z.string().uuid('Select a subject'),
    maxMarks: z.coerce.number().positive('Must be more than zero').max(1000),
    passingMarks: z.coerce.number().min(0).max(1000),
    displayOrder: z.coerce.number().int().min(0).max(999),
  })
  .refine((values) => values.passingMarks <= values.maxMarks, {
    path: ['passingMarks'],
    message: 'The pass mark cannot exceed the maximum',
  });

type FormValues = z.infer<typeof schema>;

/**
 * The papers within an examination.
 *
 * Maximum and pass marks live on the paper rather than the subject because the
 * same subject is worth 100 in a final and 20 in a class test. Papers can only
 * be added or removed while the examination is still configurable — the badge on
 * each row shows how much marking has already happened against it, which is the
 * reason removal stops being possible.
 */
export function ExaminationPapersPanel({ examination }: { examination: Examination }) {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState<ExaminationSubject | null>(null);

  const papers = examination.subjects ?? [];
  const editable = examination.configurable && can(PERMISSIONS.EXAMINATION_UPDATE);

  // The link names what this person will actually do on the other side.
  const marksLinkLabel = can(PERMISSIONS.MARKS_ENTER)
    ? 'Enter marks'
    : can(PERMISSIONS.MARKS_REVIEW)
      ? 'Review marks'
      : 'View marks';

  const curriculum = useQuery({
    queryKey: queryKeys.academicSubjects.forOffering(examination.gradeOfferingId, 'ACTIVE'),
    queryFn: () => academicSubjectsApi.list(examination.gradeOfferingId, 'ACTIVE'),
    enabled: open,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { academicSubjectId: '', maxMarks: 100, passingMarks: 40, displayOrder: 1 },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.examinations.all });
  };

  const add = useMutation({
    mutationFn: (values: FormValues) => examinationsApi.addSubject(examination.id, values),
    onSuccess: () => {
      toast.success('Paper added');
      setOpen(false);
      refresh();
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  const remove = useMutation({
    mutationFn: (paper: ExaminationSubject) =>
      examinationsApi.removeSubject(examination.id, paper.id),
    onSuccess: () => {
      toast.success('Paper removed');
      setRemoving(null);
      refresh();
    },
    onError: (error) => {
      setRemoving(null);
      handleApiError(error);
    },
  });

  // Subjects already on the examination are not offered again.
  const taken = new Set(papers.map((paper) => paper.academicSubjectId));
  const available = (curriculum.data ?? []).filter((subject) => !taken.has(subject.id));

  return (
    <>
      <Card>
        <CardHeader
          title="Papers"
          description="What is sat, and what each paper is worth."
          icon={<BookOpen className="h-4 w-4" aria-hidden="true" />}
          action={
            editable ? (
              <Button
                size="sm"
                onClick={() => {
                  form.reset({
                    academicSubjectId: '',
                    maxMarks: 100,
                    passingMarks: 40,
                    displayOrder: papers.length + 1,
                  });
                  setOpen(true);
                }}
              >
                Add paper
              </Button>
            ) : undefined
          }
        />

        {papers.length === 0 ? (
          <EmptyState
            title="No papers yet"
            description={
              editable
                ? 'Add the subjects that will be sat. Only subjects on this class’s curriculum can be chosen.'
                : 'This examination has no papers and can no longer be configured.'
            }
            icon={<BookOpen className="h-6 w-6" aria-hidden="true" />}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2">
                  <th scope="col" className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle">
                    Subject
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-subtle">
                    Max
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-subtle">
                    Pass
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle">
                    Marking
                  </th>
                  <th scope="col" className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {papers.map((paper) => (
                  <tr key={paper.id}>
                    <td className="px-4 py-3">
                      <span className="font-medium text-ink">{paper.subjectName}</span>
                      <span className="block font-mono text-[11px] text-subtle">
                        {paper.subjectCode}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-ink">{paper.maxMarks}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted">
                      {paper.passingMarks}
                    </td>
                    <td className="px-4 py-3">
                      <MarkingProgress paper={paper} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {can(PERMISSIONS.MARKS_VIEW) && (
                        <Link
                          to={ROUTES.examinationMarks(examination.id, paper.id)}
                          className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-accent transition-colors hover:bg-surface-2"
                        >
                          <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
                          {marksLinkLabel}
                        </Link>
                      )}
                      {editable && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setRemoving(paper)}
                          aria-label={`Remove ${paper.subjectName}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={open}
        title="Add a paper"
        description="Maximum and pass marks are set per paper, not per subject."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={form.handleSubmit((values) => add.mutate(values))}
              loading={add.isPending}
            >
              Add paper
            </Button>
          </>
        }
      >
        <form
          className="space-y-4"
          noValidate
          onSubmit={form.handleSubmit((values) => add.mutate(values))}
        >
          <SelectField
            label="Subject"
            required
            hint="Only subjects on this class’s own curriculum."
            error={form.formState.errors.academicSubjectId?.message}
            {...form.register('academicSubjectId')}
          >
            <option value="">Select a subject…</option>
            {available.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.subjectName}
              </option>
            ))}
          </SelectField>

          {curriculum.isSuccess && available.length === 0 && (
            <p className="text-xs text-muted">
              Every subject on this class’s curriculum already has a paper.
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              label="Maximum marks"
              type="number"
              step="0.01"
              required
              error={form.formState.errors.maxMarks?.message}
              {...form.register('maxMarks')}
            />
            <TextField
              label="Pass mark"
              type="number"
              step="0.01"
              required
              error={form.formState.errors.passingMarks?.message}
              {...form.register('passingMarks')}
            />
            <TextField
              label="Order"
              type="number"
              error={form.formState.errors.displayOrder?.message}
              {...form.register('displayOrder')}
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={removing !== null}
        title="Remove this paper?"
        message={
          removing === null
            ? ''
            : `${removing.subjectName} will be removed from this examination. This is refused if any marks have been recorded against it.`
        }
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing !== null && remove.mutate(removing)}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}

/** Entered / approved out of expected, stated in words rather than a bare ratio. */
function MarkingProgress({ paper }: { paper: ExaminationSubject }) {
  if (paper.expected === 0) {
    return <span className="text-xs text-subtle">No students due to sit it yet</span>;
  }
  const percent = Math.round((paper.marksEntered / paper.expected) * 100);
  return (
    <div className="min-w-[150px]">
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="text-muted tabular-nums">
          {paper.marksEntered} of {paper.expected} marked
        </span>
        <span className="text-subtle tabular-nums">{percent}%</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full bg-surface-3">
        <div
          className="h-full bg-accent"
          style={{ width: `${Math.min(percent, 100)}%` }}
          aria-hidden="true"
        />
      </div>
      {paper.marksApproved > 0 && (
        <span className="mt-1 block text-[11px] text-subtle tabular-nums">
          {paper.marksApproved} approved
        </span>
      )}
    </div>
  );
}
